import React, { useState, useEffect } from "react";
import { Save } from "lucide-react";
import { supabase } from "../../lib/supabase";

interface BasicInfoSectionProps {
  courseId?: string;
  initialData: any;
}

export default function BasicInfoSection({
  courseId,
  initialData,
}: BasicInfoSectionProps) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    title: "",
    slug: "",
    description: "",
    content: "",
    duration: "",
    level: "",
    max_students: 20,
    price: 0,
    is_featured: false,
    is_published: false,
    published_at: null as string | null,
  });

  // Initialize form data from props
  useEffect(() => {
    if (initialData) {
      setFormData({
        title: initialData.title || "",
        slug: initialData.slug || "",
        description: initialData.description || "",
        content: initialData.content || "",
        duration: initialData.duration || "",
        level: initialData.level || "",
        max_students: initialData.max_students || 20,
        price: initialData.price || 0,
        is_featured: Boolean(initialData.is_featured),
        is_published: Boolean(initialData.is_published),
        published_at: initialData.published_at
          ? new Date(initialData.published_at).toISOString().split("T")[0]
          : null,
      });
    }
  }, [initialData]);

  const handleSave = async () => {
    if (!courseId) return;

    try {
      setSaving(true);
      setError(null);
      setSuccessMessage(null);

      // Basic validation
      if (!formData.title.trim()) {
        throw new Error("Title is required");
      }
      if (!formData.slug.trim()) {
        throw new Error("Slug is required");
      }
      if (!formData.description.trim()) {
        throw new Error("Description is required");
      }
      if (!formData.duration.trim()) {
        throw new Error("Duration is required");
      }
      if (!formData.level.trim()) {
        throw new Error("Level is required");
      }
      if (formData.max_students < 1) {
        throw new Error("Maximum students must be at least 1");
      }
      if (formData.price < 0) {
        throw new Error("Price cannot be negative");
      }

      // Create update object with only the columns that exist in the courses table
      const updateData = {
        title: formData.title,
        slug: formData.slug,
        description: formData.description,
        content: formData.content,
        duration: formData.duration,
        level: formData.level,
        max_students: formData.max_students,
        price: formData.price,
        is_featured: formData.is_featured,
        is_published: formData.is_published,
        published_at: formData.published_at || null,
        updated_at: new Date().toISOString(),
      };

      const { error: updateError } = await supabase
        .from("courses")
        .update(updateData)
        .eq("id", courseId);

      if (updateError) throw updateError;

      // Update initialData reference after successful save
      Object.assign(initialData, formData);
      setSuccessMessage("Course updated successfully");
    } catch (err) {
      console.error("Error saving course:", err);
      setError(err instanceof Error ? err.message : "Failed to save course");
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handlePublishChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isPublished = e.target.checked;

    if (isPublished && !formData.published_at) {
      const today = new Date().toISOString().split("T")[0];
      handleInputChange("is_published", isPublished);
      handleInputChange("published_at", today);
    } else {
      handleInputChange("is_published", isPublished);
    }
  };

  return (
    <div className="bg-white shadow sm:rounded-lg">
      <div className="px-4 py-5 sm:p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-lg font-medium text-gray-900">
            Basic Information
          </h2>
          <button
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#F98B3D] hover:bg-[#e07a2c] disabled:opacity-50"
          >
            <Save className="h-5 w-5 mr-2" />
            {saving ? "Saving..." : "Save Basic Info"}
          </button>
        </div>

        {error && (
          <div className="mb-4 bg-red-50 border-l-4 border-red-400 p-4">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {successMessage && (
          <div className="mb-4 bg-green-50 border-l-4 border-green-400 p-4">
            <p className="text-sm text-green-700">{successMessage}</p>
          </div>
        )}

        <div className="grid grid-cols-1 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Title *
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => handleInputChange("title", e.target.value)}
              onBlur={() => {
                if (!formData.slug) {
                  const slug = formData.title
                    .toLowerCase()
                    .replace(/[^a-z0-9]+/g, "-")
                    .replace(/^-+|-+$/g, "");
                  handleInputChange("slug", slug);
                }
              }}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#F98B3D] focus:ring-[#F98B3D]"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Slug *
            </label>
            <input
              type="text"
              value={formData.slug}
              onChange={(e) => handleInputChange("slug", e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#F98B3D] focus:ring-[#F98B3D]"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Description *
            </label>
            <textarea
              rows={3}
              value={formData.description}
              onChange={(e) => handleInputChange("description", e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#F98B3D] focus:ring-[#F98B3D]"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Content
            </label>
            <textarea
              rows={10}
              value={formData.content}
              onChange={(e) => handleInputChange("content", e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#F98B3D] focus:ring-[#F98B3D]"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Duration *
              </label>
              <input
                type="text"
                value={formData.duration}
                onChange={(e) => handleInputChange("duration", e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#F98B3D] focus:ring-[#F98B3D]"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Level *
              </label>
              <input
                type="text"
                value={formData.level}
                onChange={(e) => handleInputChange("level", e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#F98B3D] focus:ring-[#F98B3D]"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Max Students *
              </label>
              <input
                type="number"
                value={formData.max_students}
                onChange={(e) =>
                  handleInputChange("max_students", parseInt(e.target.value))
                }
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#F98B3D] focus:ring-[#F98B3D]"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Price *
              </label>
              <input
                type="number"
                value={formData.price}
                onChange={(e) =>
                  handleInputChange("price", parseFloat(e.target.value))
                }
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#F98B3D] focus:ring-[#F98B3D]"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Published Date
            </label>
            <input
              type="date"
              value={formData.published_at || ""}
              onChange={(e) =>
                handleInputChange("published_at", e.target.value || null)
              }
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#F98B3D] focus:ring-[#F98B3D]"
            />
          </div>

          <div className="flex items-center space-x-6">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="is_featured"
                checked={formData.is_featured}
                onChange={(e) =>
                  handleInputChange("is_featured", e.target.checked)
                }
                className="h-4 w-4 text-[#F98B3D] focus:ring-[#F98B3D] border-gray-300 rounded"
              />
              <label
                htmlFor="is_featured"
                className="ml-2 block text-sm text-gray-900"
              >
                Featured course
              </label>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="is_published"
                checked={formData.is_published}
                onChange={handlePublishChange}
                className="h-4 w-4 text-[#F98B3D] focus:ring-[#F98B3D] border-gray-300 rounded"
              />
              <label
                htmlFor="is_published"
                className="ml-2 block text-sm text-gray-900"
              >
                Published
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
